import React, { useEffect, useState } from "react";
import {
  getReports,
  addReport,
  updateReport,
  deleteReport,
} from "../services/reportsService";
import { uploadFile } from "../services/storage";
import { isSupabaseConfigured } from "../supabase/client";
import { QUARTERS, QUARTER_MONTHS, fiscalDateLabel, fyLabel } from "../utils/fiscal";

// Fiscal year (Jul–Jun): `year` is the year the fiscal year ENDS in.
const currentYear = 2026; // avoids Date.now(); admins can pick any year
const YEARS = Array.from({ length: 12 }, (_, i) => currentYear - i + 1);

const emptyForm = {
  year: currentYear,
  quarter: "Q1",
  title: "",
  authors: "",
  abstract: "",
  pages: "",
  imageUrl: "",
  pdfUrl: "",
  content: "",
};

const ReportsAdmin = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pdfFile, setPdfFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [progress, setProgress] = useState({ label: "", pct: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [imgUploading, setImgUploading] = useState(false);
  const [imgNote, setImgNote] = useState("");

  // Upload an image to Supabase Storage and insert a <figure> into the article
  // content — deploy-free (the image lives in storage, not the codebase).
  const handleArticleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgUploading(true);
    setImgNote("");
    try {
      const { url } = await uploadFile(file, "article-images");
      const snippet =
        `\n<figure class="article-figure">\n` +
        `  <span class="figure-media"><img src="${url}" alt="" loading="lazy" /></span>\n` +
        `  <figcaption></figcaption>\n</figure>\n`;
      setForm((f) => ({ ...f, content: (f.content || "") + snippet }));
      setImgNote("Image uploaded and added to the content below ✓");
    } catch (err) {
      setImgNote(err.message || "Upload failed.");
    }
    setImgUploading(false);
    e.target.value = "";
  };

  const load = async () => {
    setLoading(true);
    try {
      setReports(await getReports());
    } catch (e) {
      setError("Couldn't load reports. Check your Supabase config & policies.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isSupabaseConfigured) load();
    else setLoading(false);
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setPdfFile(null);
    setImageFile(null);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      year: r.year,
      quarter: r.quarter,
      title: r.title || "",
      authors: r.authors || "",
      abstract: r.abstract || "",
      pages: r.pages || "",
      imageUrl: r.imageUrl || "",
      pdfUrl: r.pdfUrl || "",
      content: r.content || "",
    });
    setPdfFile(null);
    setImageFile(null);
    setError("");
    setModalOpen(true);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!editingId && !pdfFile) return setError("Please attach the report PDF.");
    if (!editingId && !imageFile && !form.imageUrl)
      return setError("Please attach a header image.");

    setSaving(true);
    try {
      let { imageUrl, pdfUrl } = form;

      if (imageFile) {
        setProgress({ label: "Uploading image…", pct: 100 });
        const res = await uploadFile(imageFile, "reports/images");
        imageUrl = res.url;
      }

      if (pdfFile) {
        setProgress({ label: "Uploading PDF…", pct: 100 });
        const res = await uploadFile(pdfFile, "reports/pdfs");
        pdfUrl = res.url;
      }

      const payload = {
        year: Number(form.year),
        quarter: form.quarter,
        date: fiscalDateLabel(form.quarter, form.year),
        title: form.title.trim(),
        authors: form.authors.trim(),
        abstract: form.abstract.trim(),
        pages: form.pages ? Number(form.pages) : null,
        imageUrl,
        pdfUrl,
        content: form.content,
      };

      if (editingId) await updateReport(editingId, payload);
      else await addReport(payload);

      setModalOpen(false);
      setProgress({ label: "", pct: 0 });
      await load();
    } catch (err) {
      setError(err.message || "Something went wrong while saving.");
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the ${r.quarter} ${fyLabel(r.year)} report? This can't be undone.`)) return;
    await deleteReport(r.id);
    await load();
  };

  return (
    <>
      <div className="admin-topbar">
        <h2>Management Reports</h2>
        <button className="btn btn-primary" onClick={openAdd} disabled={!isSupabaseConfigured}>
          + Add Report
        </button>
      </div>

      <div className="admin-content">
        {!isSupabaseConfigured && (
          <div className="config-warning">
            Add your Supabase keys to <code>.env</code> to enable editing.
          </div>
        )}
        {error && !modalOpen && <div className="admin-alert error">{error}</div>}

        <div className="admin-panel">
          <div className="admin-panel-head">
            <h3>{reports.length} report{reports.length === 1 ? "" : "s"}</h3>
          </div>

          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : reports.length === 0 ? (
            <div className="admin-empty">No reports yet. Click “Add Report” to publish one.</div>
          ) : (
            <ul className="admin-list">
              {reports.map((r) => (
                <li className="admin-list-item" key={r.id}>
                  {r.imageUrl ? (
                    <img
                      className="admin-list-thumb"
                      src={r.imageUrl}
                      alt=""
                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                    />
                  ) : (
                    <div className="admin-list-thumb" />
                  )}
                  <div className="admin-list-main">
                    <p className="t">{r.title}</p>
                    <p className="m">{r.quarter} · {fyLabel(r.year)} — {r.date} · {r.authors}</p>
                  </div>
                  <div className="admin-list-actions">
                    <button className="btn btn-ghost" onClick={() => openEdit(r)}>Edit</button>
                    <button className="btn btn-danger-text" onClick={() => handleDelete(r)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="admin-modal-backdrop" onMouseDown={() => !saving && setModalOpen(false)}>
          <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Report" : "Add Report"}</h3>
            {error && <div className="admin-alert error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="admin-row">
                <div className="admin-field">
                  <label>Fiscal year</label>
                  <select value={form.year} onChange={setField("year")}>
                    {YEARS.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
                  </select>
                </div>
                <div className="admin-field">
                  <label>Quarter</label>
                  <select value={form.quarter} onChange={setField("quarter")}>
                    {QUARTERS.map((q) => (
                      <option key={q} value={q}>{q} ({QUARTER_MONTHS[q]})</option>
                    ))}
                  </select>
                </div>
                <div className="admin-field">
                  <label>Pages</label>
                  <input type="number" min="1" value={form.pages} onChange={setField("pages")} placeholder="e.g. 7" />
                </div>
              </div>

              <div className="admin-field">
                <label>Title</label>
                <input value={form.title} onChange={setField("title")} required placeholder="From Stabilization to Shock Management" />
              </div>

              <div className="admin-field">
                <label>Authors</label>
                <input value={form.authors} onChange={setField("authors")} placeholder="Mudassar Ali & Hassan Ali" />
              </div>

              <div className="admin-field">
                <label>Abstract</label>
                <textarea value={form.abstract} onChange={setField("abstract")} placeholder="Short summary shown on the card…" />
              </div>

              <div className="admin-field">
                <label>Header image {editingId && "(leave empty to keep current)"}</label>
                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                {form.imageUrl && !imageFile && <div className="hint">Current image will be kept.</div>}
              </div>

              <div className="admin-field">
                <label>Report PDF {editingId && "(leave empty to keep current)"}</label>
                <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files[0])} />
                {form.pdfUrl && !pdfFile && <div className="hint">Current PDF will be kept.</div>}
              </div>

              <div className="admin-field">
                <label>Article content <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional — leave blank to show the PDF)</span></label>
                <textarea
                  value={form.content}
                  onChange={setField("content")}
                  rows={6}
                  placeholder="Paste the formatted article content here to publish it as a web article instead of the PDF."
                  style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82rem" }}
                />
                <div className="hint">When filled, the report opens as a styled article on the site. When empty, it opens as the PDF. Changes go live instantly — no redeploy.</div>
              </div>

              <div className="admin-field">
                <label>Add an image to the article</label>
                <input type="file" accept="image/*" onChange={handleArticleImage} disabled={imgUploading} />
                {imgUploading && <div className="hint">Uploading…</div>}
                {imgNote && <div className="hint">{imgNote}</div>}
                <div className="hint">Uploads to storage and inserts a figure at the end of the content above — move it where you want. No deploy needed.</div>
              </div>

              {saving && progress.label && (
                <div className="upload-box">
                  <div className="upload-current">
                    <span className="admin-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    {progress.label}
                  </div>
                </div>
              )}

              <div className="admin-modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Publish Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsAdmin;
